import { useState, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@/hooks/useAuth'
import { useProfile } from '@/hooks/useProfile'
import { ONBOARDING_STEP_NAMES } from '@/hooks/useOnboarding'
import apiClient from '@/lib/api-client'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Card, CardContent } from '@/components/ui/Card'
import {
  User,
  Camera,
  MapPin,
  Image,
  Briefcase,
  Calendar,
  CheckCircle,
  ArrowRight,
  ArrowLeft,
  X,
  Upload,
  Plus,
  Clock,
} from 'lucide-react'

const ONBOARDING_COMPLETED_KEY = 'onboarding_completed'
const ONBOARDING_SKIPPED_STEPS_KEY = 'onboarding_skipped_steps'

interface ProfileCompletionWizardProps {
  isOpen: boolean
  onComplete: () => void
}

interface Step {
  id: string
  titleKey: string
  descriptionKey: string
  icon: React.ElementType
  skippable: boolean
}

export function ProfileCompletionWizard({ isOpen, onComplete }: ProfileCompletionWizardProps) {
  const { t } = useTranslation('dashboard')
  const { user } = useAuth()
  const { updateProfile } = useProfile()

  const STEPS: Step[] = [
    {
      id: ONBOARDING_STEP_NAMES.STEP_1,
      titleKey: t('onboarding.steps.businessDetails.title'),
      descriptionKey: t('onboarding.steps.businessDetails.description'),
      icon: User,
      skippable: false,
    },
    {
      id: ONBOARDING_STEP_NAMES.STEP_2,
      titleKey: t('onboarding.steps.profilePhoto.title'),
      descriptionKey: t('onboarding.steps.profilePhoto.description'),
      icon: Camera,
      skippable: true,
    },
    {
      id: ONBOARDING_STEP_NAMES.STEP_3,
      titleKey: t('onboarding.steps.bioLocation.title'),
      descriptionKey: t('onboarding.steps.bioLocation.description'),
      icon: MapPin,
      skippable: true,
    },
    {
      id: ONBOARDING_STEP_NAMES.STEP_4,
      titleKey: t('onboarding.steps.gallery.title'),
      descriptionKey: t('onboarding.steps.gallery.description'),
      icon: Image,
      skippable: true,
    },
    {
      id: ONBOARDING_STEP_NAMES.STEP_5,
      titleKey: t('onboarding.steps.services.title'),
      descriptionKey: t('onboarding.steps.services.description'),
      icon: Briefcase,
      skippable: true,
    },
    {
      id: ONBOARDING_STEP_NAMES.STEP_6,
      titleKey: t('onboarding.steps.availability.title'),
      descriptionKey: t('onboarding.steps.availability.description'),
      icon: Calendar,
      skippable: true,
    },
  ]

  const [currentStep, setCurrentStep] = useState(0)
  const [isLoading, setIsLoading] = useState(false)
  const [skippedSteps, setSkippedSteps] = useState<string[]>([])

  // Step 1: Business details
  const [businessName, setBusinessName] = useState('')
  const [tagline, setTagline] = useState('')

  // Step 2: Profile photo
  const [photoFile, setPhotoFile] = useState<File | null>(null)
  const [photoPreview, setPhotoPreview] = useState<string | null>(null)

  // Step 3: Bio & Location
  const [bio, setBio] = useState('')
  const [location, setLocation] = useState('')

  // Step 4: Gallery
  const [galleryFiles, setGalleryFiles] = useState<File[]>([])

  // Load skipped steps from localStorage on mount
  useEffect(() => {
    if (isOpen) {
      const savedSkipped = localStorage.getItem(ONBOARDING_SKIPPED_STEPS_KEY)
      if (savedSkipped) {
        try {
          setSkippedSteps(JSON.parse(savedSkipped))
        } catch {
          setSkippedSteps([])
        }
      }
    }
  }, [isOpen])

  // Save skipped steps to localStorage
  const saveSkippedSteps = useCallback((skipped: string[]) => {
    localStorage.setItem(ONBOARDING_SKIPPED_STEPS_KEY, JSON.stringify(skipped))
  }, [])

  // Handle photo upload
  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      setPhotoFile(file)
      const reader = new FileReader()
      reader.onloadend = () => {
        setPhotoPreview(reader.result as string)
      }
      reader.readAsDataURL(file)
    }
  }

  // Handle gallery upload
  const handleGalleryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || [])
    if (files.length > 0) {
      setGalleryFiles((prev) => [...prev, ...files])
    }
  }

  // Upload gallery images
  const uploadGalleryImages = async (files: File[]) => {
    if (files.length === 0) return

    const formData = new FormData()
    files.forEach((file) => {
      formData.append('images', file)
    })

    try {
      await apiClient.post('/profile/gallery', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
    } catch (error) {
      console.error('Failed to upload gallery images:', error)
    }
  }

  // Mark step as skipped
  const handleSkipStep = () => {
    const stepId = STEPS[currentStep].id
    if (!skippedSteps.includes(stepId)) {
      const newSkipped = [...skippedSteps, stepId]
      setSkippedSteps(newSkipped)
      saveSkippedSteps(newSkipped)
    }
    moveToNext()
  }

  // Move to next step
  const moveToNext = async () => {
    if (currentStep < STEPS.length - 1) {
      setCurrentStep(currentStep + 1)
    } else {
      // Complete onboarding
      localStorage.setItem(ONBOARDING_COMPLETED_KEY, 'true')
      await updateProfile()
      onComplete()
    }
  }

  // Handle next button click
  const handleNext = async () => {
    setIsLoading(true)

    try {
      if (currentStep === 0) {
        // Save business details
        await apiClient.put('/profile', {
          business_name: businessName,
          tagline: tagline,
        })
      } else if (currentStep === 1) {
        // Upload profile photo
        if (photoFile) {
          const formData = new FormData()
          formData.append('profile_image', photoFile)
          await apiClient.post('/profile/photo', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
          })
        }
      } else if (currentStep === 2) {
        // Save bio & location
        await apiClient.put('/profile', {
          bio: bio,
          location: location,
        })
      } else if (currentStep === 3) {
        // Upload gallery images
        await uploadGalleryImages(galleryFiles)
      } else if (currentStep === 4) {
        // Services step - nothing to save here (will be handled by services page)
      } else if (currentStep === 5) {
        // Availability step - nothing to save here (will be handled by availability page)
      }

      await updateProfile()
      moveToNext()
    } catch (error) {
      console.error(`Failed to save step ${currentStep + 1}:`, error)
      // Still move to next step even if save failed
      moveToNext()
    } finally {
      setIsLoading(false)
    }
  }

  // Handle back button
  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1)
    }
  }

  // Handle close/cancel
  const handleClose = () => {
    // Save current progress before closing
    localStorage.setItem(ONBOARDING_COMPLETED_KEY, 'true')
    onComplete()
  }

  if (!isOpen) return null

  const currentStepData = STEPS[currentStep]
  const StepIcon = currentStepData.icon
  const canGoBack = currentStep > 0
  const isLastStep = currentStep === STEPS.length - 1

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b dark:border-gray-700">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center">
              <span className="text-white font-bold text-sm">
                {user?.first_name?.[0] || user?.email?.[0] || '!'}
              </span>
            </div>
            <span className="font-semibold text-gray-900 dark:text-white">
              {t('onboarding.welcome', { name: user?.first_name || '', comma: user?.first_name ? ', ' : '' })}
            </span>
          </div>
          <button
            onClick={handleClose}
            className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Progress bar */}
        <div className="flex">
          {STEPS.map((step, index) => (
            <div
              key={step.id}
              className={`flex-1 h-1 transition-colors ${
                index <= currentStep
                  ? 'bg-primary'
                  : skippedSteps.includes(step.id)
                  ? 'bg-yellow-400'
                  : 'bg-gray-200 dark:bg-gray-700'
              }`}
              title={skippedSteps.includes(step.id) ? `Skipped: ${step.titleKey}` : step.titleKey}
            />
          ))}
        </div>

        {/* Step indicator */}
        <div className="px-4 pt-3">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {t('onboarding.stepIndicator', { current: currentStep + 1, total: STEPS.length })}
          </p>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="text-center mb-6">
            <div className="w-12 h-12 rounded-full bg-primary/10 dark:bg-primary/20 flex items-center justify-center mx-auto mb-4">
              <StepIcon className="w-6 h-6 text-primary" />
            </div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-1">
              {currentStepData.titleKey}
            </h2>
            <p className="text-gray-500 dark:text-gray-400">
              {currentStepData.descriptionKey}
            </p>
          </div>

          {/* Step 1: Business Details */}
          {currentStep === 0 && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('onboarding.steps.businessDetails.businessName')} <span className="text-red-500">*</span>
                </label>
                <Input
                  value={businessName}
                  onChange={(e) => setBusinessName(e.target.value)}
                  placeholder={t('onboarding.steps.businessDetails.businessNamePlaceholder')}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('onboarding.steps.businessDetails.tagline')}
                </label>
                <Input
                  value={tagline}
                  onChange={(e) => setTagline(e.target.value)}
                  placeholder={t('onboarding.steps.businessDetails.taglinePlaceholder')}
                  maxLength={80}
                />
                <p className="mt-1 text-xs text-gray-400">
                  {t('onboarding.steps.businessDetails.taglineChars', { count: tagline.length })}
                </p>
              </div>
            </div>
          )}

          {/* Step 2: Profile Photo */}
          {currentStep === 1 && (
            <div className="text-center">
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                {t('onboarding.steps.profilePhoto.description')}
              </p>
              <Card className="border-dashed border-2 dark:border-gray-600">
                <CardContent className="py-6">
                  {photoPreview ? (
                    <div className="relative inline-block">
                      <img
                        src={photoPreview}
                        alt="Profile preview"
                        className="w-32 h-32 rounded-full object-cover mx-auto"
                      />
                      <label className="absolute bottom-0 right-0 bg-primary text-white p-2 rounded-full cursor-pointer hover:bg-primary/90">
                        <Camera className="w-4 h-4" />
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handlePhotoChange}
                          className="hidden"
                        />
                      </label>
                    </div>
                  ) : (
                    <label className="cursor-pointer block">
                      <div className="w-24 h-24 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center mx-auto mb-3">
                        <User className="w-10 h-10 text-gray-400" />
                      </div>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {t('onboarding.steps.profilePhoto.uploadPrompt')}
                      </p>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handlePhotoChange}
                        className="hidden"
                      />
                    </label>
                  )}
                </CardContent>
              </Card>
              <button
                type="button"
                onClick={handleSkipStep}
                className="mt-4 text-sm text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
              >
                {t('onboarding.steps.profilePhoto.skip')}
              </button>
            </div>
          )}

          {/* Step 3: Bio & Location */}
          {currentStep === 2 && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('onboarding.steps.bioLocation.bio')}
                </label>
                <textarea
                  value={bio}
                  onChange={(e) => setBio(e.target.value)}
                  placeholder={t('onboarding.steps.bioLocation.bioPlaceholder')}
                  className="flex min-h-[100px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-gray-800 dark:border-gray-700"
                  maxLength={500}
                />
                <p className="mt-1 text-xs text-gray-400">
                  {t('onboarding.steps.bioLocation.bioChars', { count: bio.length })}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('onboarding.steps.bioLocation.location')}
                </label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                    placeholder={t('onboarding.steps.bioLocation.locationPlaceholder')}
                    className="pl-10"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Step 4: Gallery Images */}
          {currentStep === 3 && (
            <div className="text-center">
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                {t('onboarding.steps.gallery.description')}
              </p>
              <Card className="border-dashed border-2 dark:border-gray-600">
                <CardContent className="py-8">
                  <Upload className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
                    {t('onboarding.steps.gallery.uploadPrompt')}
                  </p>
                  <label className="cursor-pointer">
                    <span className="inline-flex items-center justify-center px-4 py-2 bg-primary/10 text-primary rounded-md hover:bg-primary/20 transition-colors text-sm font-medium">
                      {t('onboarding.steps.gallery.selectFiles')}
                    </span>
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handleGalleryChange}
                      className="hidden"
                    />
                  </label>
                </CardContent>
              </Card>
              {galleryFiles.length > 0 && (
                <p className="mt-2 text-sm text-green-600 dark:text-green-400">
                  {t('onboarding.steps.gallery.filesSelected', { count: galleryFiles.length })}
                </p>
              )}
              <button
                type="button"
                onClick={handleSkipStep}
                className="mt-4 text-sm text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
              >
                {t('onboarding.steps.gallery.skip')}
              </button>
            </div>
          )}

          {/* Step 5: Services */}
          {currentStep === 4 && (
            <div className="text-center">
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                {t('onboarding.steps.services.description')}
              </p>
              <Card className="border-dashed border-2 dark:border-gray-600">
                <CardContent className="py-8">
                  <Briefcase className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
                    {t('onboarding.steps.services.addFirstService')}
                  </p>
                  <Button variant="outline" size="sm">
                    <Plus className="w-4 h-4 mr-2" />
                    {t('onboarding.steps.services.addService')}
                  </Button>
                </CardContent>
              </Card>
              <button
                type="button"
                onClick={handleSkipStep}
                className="mt-4 text-sm text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
              >
                {t('onboarding.steps.services.skip')}
              </button>
            </div>
          )}

          {/* Step 6: Availability */}
          {currentStep === 5 && (
            <div className="text-center">
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                {t('onboarding.steps.availability.description')}
              </p>
              <Card className="border-dashed border-2 dark:border-gray-600">
                <CardContent className="py-8">
                  <Clock className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
                    {t('onboarding.steps.availability.setAvailability')}
                  </p>
                  <Button variant="outline" size="sm">
                    <Calendar className="w-4 h-4 mr-2" />
                    {t('onboarding.steps.availability.configureHours')}
                  </Button>
                </CardContent>
              </Card>
              <button
                type="button"
                onClick={handleSkipStep}
                className="mt-4 text-sm text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
              >
                {t('onboarding.steps.availability.skip')}
              </button>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-4 border-t bg-gray-50 dark:bg-gray-800 dark:border-gray-700">
          <Button
            variant="ghost"
            onClick={handleBack}
            disabled={!canGoBack}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {t('onboarding.buttons.back')}
          </Button>

          <div className="flex items-center gap-2">
            {currentStepData.skippable && (
              <Button
                variant="ghost"
                onClick={handleSkipStep}
                disabled={isLoading}
              >
                {t('onboarding.buttons.skip')}
              </Button>
            )}
            <Button
              onClick={handleNext}
              disabled={
                isLoading ||
                (currentStep === 0 && !businessName.trim())
              }
            >
              {isLoading ? (
                t('onboarding.buttons.saving')
              ) : isLastStep ? (
                <>
                  {t('onboarding.buttons.finish')}
                  <CheckCircle className="w-4 h-4 ml-2" />
                </>
              ) : (
                <>
                  {t('onboarding.buttons.next')}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}
